import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ClockCounterClockwise, Image, VideoCamera, UserCircle, Stack, Download, Funnel } from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { downloadFile } from '../utils/share';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const C = { card: '#141414', border: 'rgba(255,255,255,0.07)', text: '#f5f0e8', dim: 'rgba(245,240,232,0.4)', accent: '#c8f060' };

const TYPE_CONFIG = {
  flyer: { label: 'Flyer', color: '#c8f060', icon: Image },
  ai_flyer: { label: 'Custom Flyer', color: '#a78bfa', icon: Image },
  carousel: { label: 'Carousel', color: '#22d3ee', icon: Stack },
  video: { label: 'Video', color: '#f59e0b', icon: VideoCamera },
  character: { label: 'Character', color: '#ec4899', icon: UserCircle },
};

export default function ContentHistoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchHistory = useCallback(async () => {
    try {
      const token = localStorage.getItem('bf_access_token') || localStorage.getItem('token');
      const { data } = await axios.get(`${API}/api/content-history?limit=100`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.items || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const filtered = filter === 'all' ? items : items.filter(i => i.type === filter);

  return (
    <DashboardLayout>
      <div data-testid="content-history-page" className="space-y-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(200,240,96,0.06)', border: `1px solid ${C.border}` }}>
              <ClockCounterClockwise size={20} style={{ color: C.accent }} />
            </div>
            <div>
              <h1 className="text-2xl font-medium font-['Instrument_Serif',serif]" style={{ color: C.text }}>Content History</h1>
              <p className="text-sm" style={{ color: C.dim }}>{items.length} items created</p>
            </div>
          </div>
        </motion.div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2 flex-wrap">
          <Funnel size={14} style={{ color: C.dim }} />
          {['all', 'flyer', 'ai_flyer', 'carousel', 'video', 'character'].map(f => {
            const cfg = TYPE_CONFIG[f] || { label: 'All', color: C.accent };
            return (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                className="px-3 py-1.5 rounded-lg text-[11px] font-medium capitalize transition-all"
                style={{
                  background: filter === f ? `${cfg.color || C.accent}12` : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${filter === f ? `${cfg.color || C.accent}30` : C.border}`,
                  color: filter === f ? (cfg.color || C.accent) : C.dim,
                }}>
                {f === 'all' ? `All (${items.length})` : `${cfg.label} (${items.filter(i => i.type === f).length})`}
              </button>
            );
          })}
        </div>

        {/* Content grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 rounded-xl" style={{ background: C.card, border: `1px solid ${C.border}` }}>
            <ClockCounterClockwise size={40} className="mx-auto mb-4" style={{ color: C.dim }} />
            <p className="text-sm" style={{ color: C.dim }}>No content yet. Start creating!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((item, i) => {
              const cfg = TYPE_CONFIG[item.type] || { label: item.type, color: '#888', icon: Image };
              const IconComp = cfg.icon;
              return (
                <motion.div key={`${item.type}-${i}`} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.5) }}
                  className="rounded-xl p-4 flex flex-col" style={{ background: C.card, border: `1px solid ${C.border}` }}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${cfg.color}12` }}>
                        <IconComp size={14} weight="fill" style={{ color: cfg.color }} />
                      </div>
                      <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.label}</span>
                    </div>
                    {item.download_url && (
                      <button onClick={() => downloadFile(`${API}${item.download_url}`, `${item.title || 'content'}.png`)}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-all" data-testid={`download-${i}`}>
                        <Download size={14} style={{ color: C.dim }} />
                      </button>
                    )}
                  </div>
                  <p className="text-sm font-medium mb-1 line-clamp-2" style={{ color: C.text }}>{item.title || 'Untitled'}</p>
                  <p className="text-[10px] mt-auto pt-2" style={{ color: C.dim }}>
                    {item.created_at ? new Date(item.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                    {item.format ? ` — ${item.format}` : ''}
                  </p>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
